import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Mail, 
  Phone, 
  MapPin, 
  Linkedin, 
  GraduationCap, 
  Briefcase, 
  FileText, 
  Video, 
  Edit, 
  Check, 
  X,
  Upload,
  Loader 
} from 'lucide-react';
import { gql, useMutation } from '@apollo/client';
import Navbar from '../Navbar';

// GraphQL Mutation for file uploads
const UPLOAD_STUDENT_FILE = gql`
  mutation UploadStudentFile($file: Upload!, $fileType: String!) {
    upload_student_file(file: $file, file_type: $fileType) {
      url
      success
      message
    }
  }
`;

// Progress Circle Component
const ProgressCircle = ({ value, size = 100, primaryColor }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (value / 100) * circumference;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="#e5e7eb"
          strokeWidth={strokeWidth}
          fill="none"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={primaryColor || '#5489B6'}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          className="transition-all duration-500"
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-2xl font-bold">{value}%</span>
      </div>
    </div>
  );
};

const StudentProfile = ({
  currentUser,
  studentProfile,
  editMode: initialEditMode = false,
  onSave,
  brandLogoUrl,
  primaryColor,
  secondaryColor,
  accentColor,
  neutralColor,
}) => {
  const [isEditing, setIsEditing] = useState(initialEditMode);
  const [formData, setFormData] = useState({
    candidateTrack: studentProfile?.candidate_track || 'student',
    phone: studentProfile?.phone || '',
    city: studentProfile?.city || '',
    state: studentProfile?.state || '',
    school: studentProfile?.school || '',
    degreeProgram: studentProfile?.degree_program || '',
    graduationYear: studentProfile?.graduation_year || '',
    employmentType: studentProfile?.employment_type || 'internship',
    experienceLevel: studentProfile?.experience_level || '',
    desiredLocation: studentProfile?.desired_location || '',
    candidateInterests: studentProfile?.candidate_interests || [],
    whyInterested: studentProfile?.why_interested || '',
    linkedinUrl: studentProfile?.linkedin_url || '',
    certifications: studentProfile?.certifications || [],
  });

  const [certificationInput, setCertificationInput] = useState('');
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSuccess, setUploadSuccess] = useState('');
  const profileCompletion = studentProfile?.profile_completion_percentage || 0;
  const isEmployeeCandidate = (studentProfile?.candidate_track || 'student') === 'employee';

  const colors = useMemo(
    () => ({
      primary: primaryColor || '#5489B6',
      secondary: secondaryColor || '#002949',
      accent: accentColor || '#5B5E5A',
      neutral: neutralColor || '#666666',
    }),
    [primaryColor, secondaryColor, accentColor, neutralColor],
  );

  // File upload mutation
  const [uploadFile] = useMutation(UPLOAD_STUDENT_FILE);

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const addCertification = () => {
    if (certificationInput.trim() && !formData.certifications.includes(certificationInput.trim())) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, certificationInput.trim()],
      });
      setCertificationInput('');
    }
  };

  const removeCertification = (cert) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((c) => c !== cert),
    });
  };

  const handleFileUpload = async (e, fileType) => {
    const file = e.target.files && e.target.files[0];
    const inputElement = e.target; // Store reference before async operations
    
    if (!file) return;

    // Clear previous messages
    setUploadError('');
    setUploadSuccess('');

    // Validate file size
    const maxSizes = {
      resume: 5 * 1024 * 1024,
      video: 50 * 1024 * 1024,
      avatar: 2 * 1024 * 1024,
    };
    const maxSize = maxSizes[fileType];
    const maxSizeLabel = { resume: '5MB', video: '50MB', avatar: '2MB' }[fileType];
    
    if (file.size > maxSize) {
      setUploadError(`File size exceeds ${maxSizeLabel} limit`);
      if (inputElement) inputElement.value = '';
      return;
    }

    // Validate file type
    if (fileType === 'resume' && !file.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Resume must be a PDF file');
      if (inputElement) inputElement.value = '';
      return;
    }

    if (fileType === 'video') {
      const validExtensions = ['.mp4', '.mov', '.avi', '.wmv'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!hasValidExtension) {
        setUploadError('Video must be in MP4, MOV, AVI, or WMV format');
        if (inputElement) inputElement.value = '';
        return;
      }
    }

    if (fileType === 'avatar') {
      const validExtensions = ['.jpg', '.jpeg', '.png', '.gif'];
      const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
      if (!hasValidExtension) {
        setUploadError('Avatar must be in JPG, PNG, or GIF format');
        if (inputElement) inputElement.value = '';
        return;
      }
    }

    try {
      // Set appropriate loading state
      if (fileType === 'resume') {
        setUploadingResume(true);
      } else if (fileType === 'video') {
        setUploadingVideo(true);
      } else if (fileType === 'avatar') {
        setUploadingAvatar(true);
      }

      const result = await uploadFile({
        variables: {
          file: file,
          fileType: fileType,
        },
        context: {
          hasUpload: true,
        },
      });

      if (result.data?.upload_student_file?.success) {
        setUploadSuccess(result.data.upload_student_file.message || `${fileType} uploaded successfully!`);
        // Reload profile data
        setTimeout(() => {
          window.location.reload(); // Simple reload to fetch updated data
        }, 1000);
      } else {
        setUploadError('Upload failed. Please try again.');
      }
    } catch (error) {
      console.error(`${fileType} upload error:`, error);
      setUploadError(error.message || 'Upload failed. Please try again.');
    } finally {
      if (fileType === 'resume') {
        setUploadingResume(false);
      } else if (fileType === 'video') {
        setUploadingVideo(false);
      } else if (fileType === 'avatar') {
        setUploadingAvatar(false);
      }
      // Clear the input (check if element still exists)
      if (inputElement) {
        inputElement.value = '';
      }
    }
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(formData);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setFormData({
      candidateTrack: studentProfile?.candidate_track || 'student',
      phone: studentProfile?.phone || '',
      city: studentProfile?.city || '',
      state: studentProfile?.state || '',
      school: studentProfile?.school || '',
      degreeProgram: studentProfile?.degree_program || '',
      graduationYear: studentProfile?.graduation_year || '',
      employmentType: studentProfile?.employment_type || 'internship',
      experienceLevel: studentProfile?.experience_level || '',
      desiredLocation: studentProfile?.desired_location || '',
      candidateInterests: studentProfile?.candidate_interests || [],
      whyInterested: studentProfile?.why_interested || '',
      linkedinUrl: studentProfile?.linkedin_url || '',
      certifications: studentProfile?.certifications || [],
    });
    setIsEditing(false);
  };

  if (!isEditing) {
    // View Mode - Pixel-perfect match to design
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar
          currentUser={currentUser}
          currentPath="/student/profile"
          brandLogoUrl={brandLogoUrl}
          primaryColor={colors.primary}
          secondaryColor={colors.secondary}
        />
        <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <h1 className="text-3xl font-bold">My Profile</h1>
            <button
              onClick={() => setIsEditing(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-white rounded-lg transition-colors"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Edit className="h-5 w-5" />
              Edit Profile
            </button>
          </div>

          {/* Profile Completion */}
          <div
            className="bg-gradient-to-r border rounded-lg p-6 mb-6"
            style={{
              backgroundImage: `linear-gradient(90deg, ${colors.primary}0f, ${colors.primary}1a)`,
              borderColor: `${colors.primary}33`,
            }}
          >
            <div className="flex items-center gap-6">
              <ProgressCircle value={profileCompletion} size={100} primaryColor={colors.primary} />

              <div className="flex-1">
                <h3 className="font-semibold text-lg mb-2">Profile Completion</h3>
                <p className="text-sm text-gray-600 mb-3">
                  {profileCompletion}% complete. Add a resume and video introduction to reach 100%!
                </p>
                <div className="flex gap-2">
                  {!studentProfile?.resume_url && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      Resume Missing
                    </span>
                  )}
                  {!studentProfile?.video_url && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                      Video Missing
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Profile Card */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mb-6">
              {/* Avatar */}
              <div className="flex-shrink-0">
                {studentProfile?.avatar_url ? (
                  <img
                    src={studentProfile.avatar_url}
                    alt={currentUser?.name}
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {currentUser?.name?.charAt(0) || 'S'}
            </div>
          )}
        </div>

              <div className="flex-1 text-center md:text-left">
                <h2 className="text-2xl font-bold mb-2">{currentUser?.name}</h2>
                <p className="text-lg text-gray-600 mb-1">
                  {isEmployeeCandidate ? 'Experienced candidate' : (studentProfile?.school || 'School not provided')}
                </p>
                <p className="text-gray-600 mb-4">
                  {isEmployeeCandidate
                    ? (studentProfile?.experience_level || 'Experience level not provided')
                    : `Class of ${studentProfile?.graduation_year || 'Not provided'}`}
                </p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    isEmployeeCandidate ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'
                  }`}>
                    {isEmployeeCandidate ? 'Employee Candidate' : 'Student Candidate'}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-900 text-white">
                    Open to Both
                  </span>
                  <span
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                    style={{ backgroundColor: `${colors.primary}1a`, color: colors.primary }}
                  >
                    Available
                  </span>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="font-semibold mb-4">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium">{currentUser?.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Phone</p>
                    <p className="font-medium">{studentProfile?.phone || '(650) 555-0123'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">
                      {studentProfile?.city && studentProfile?.state
                        ? `${studentProfile.city}, ${studentProfile.state}`
                        : 'Palo Alto, CA'}
                    </p>
                  </div>
                </div>
                {formData.linkedinUrl && (
                  <div className="flex items-center gap-3">
                    <Linkedin className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm text-gray-500">LinkedIn</p>
                      <a
                        href={formData.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline"
                        style={{ color: colors.primary }}
                      >
                        View Profile
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Education / Professional Background */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                {isEmployeeCandidate ? 'Professional Background' : 'Education'}
              </h3>
              <div className="space-y-2">
                {isEmployeeCandidate ? (
                  <>
                    <p className="font-medium">Experience Level</p>
                    <p className="text-gray-600">{studentProfile?.experience_level || 'Not provided yet'}</p>
                  </>
                ) : (
                  <>
                    <p className="font-medium">{studentProfile?.school || 'Not provided yet'}</p>
                    <p className="text-gray-600">
                      {studentProfile?.degree_program || 'Not provided yet'}
                    </p>
                    <p className="text-sm text-gray-500">
                      Expected Graduation: {studentProfile?.graduation_year || 'Not provided yet'}
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Career Interests */}
            <div className="border-t border-gray-200 pt-6 mb-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Career Interests
              </h3>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Preferred Location</p>
                  <p className="font-medium">
                    {studentProfile?.desired_location || 'San Francisco, CA or Remote'}
                  </p>
              </div>
              <div>
                  <p className="text-sm text-gray-500 mb-1">{isEmployeeCandidate ? 'Interests' : 'Availability'}</p>
                  <p className="font-medium">
                    {isEmployeeCandidate
                      ? ((studentProfile?.candidate_interests || []).join(', ') || 'Not provided yet')
                      : 'Within 3 months, After graduation'}
                  </p>
                </div>
              </div>
            </div>

            {/* Certifications */}
            {formData.certifications.length > 0 && (
              <div className="border-t border-gray-200 pt-6 mb-6">
                <h3 className="font-semibold mb-4">Certifications</h3>
                <div className="flex flex-wrap gap-2">
                  {formData.certifications.map((cert) => (
                    <span
                      key={cert}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                    >
                      {cert}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Why Interested */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold mb-4">
                {isEmployeeCandidate
                  ? "Why I'm interested in joining an SEIC member firm"
                  : "Why I'm Interested in Financial Services"}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {studentProfile?.why_interested || 'Not provided yet'}
              </p>
            </div>
          </div>

          {/* Resume & Video */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Resume
              </h3>
              {studentProfile?.resume_url ? (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8" style={{ color: colors.primary }} />
                    <div>
                      <p className="font-medium text-sm">Resume.pdf</p>
                      <p className="text-xs text-gray-500">Uploaded</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                    Download
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No resume uploaded yet</p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Video className="h-5 w-5" />
                Video Introduction
              </h3>
              {studentProfile?.video_url ? (
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Video className="h-8 w-8" style={{ color: colors.primary }} />
                    <div>
                      <p className="font-medium text-sm">Introduction.mp4</p>
                      <p className="text-xs text-gray-500">Uploaded</p>
                    </div>
                  </div>
                  <button className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                    Watch
                  </button>
                </div>
              ) : (
                <p className="text-sm text-gray-500">No video uploaded yet</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit Mode - Pixel-perfect match to design
  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <Navbar
        currentUser={currentUser}
        currentPath="/student/profile"
        brandLogoUrl={brandLogoUrl}
        primaryColor={colors.primary}
        secondaryColor={colors.secondary}
      />
      <div className="container mx-auto px-4 py-6 md:py-8 max-w-4xl">
        {/* Header */}
        <div className="flex justify-between items-start mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Edit Profile</h1>
            <p className="text-gray-600">
              {isEmployeeCandidate
                ? 'Update your professional profile so advisor firms can evaluate your background'
                : 'Update your information to attract advisors'}
            </p>
            <span className={`inline-flex items-center mt-2 px-2.5 py-1 rounded text-xs font-semibold ${
              isEmployeeCandidate ? 'bg-indigo-100 text-indigo-700' : 'bg-sky-100 text-sky-700'
            }`}>
              {isEmployeeCandidate ? 'Employee Candidate' : 'Student Candidate'}
            </span>
          </div>
        </div>

        {/* Edit Form */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6 space-y-6">
          {/* Profile Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Profile Photo</label>
            <div className="flex items-center gap-6">
              {/* Avatar Preview */}
              <div className="relative">
                {studentProfile?.avatar_url ? (
                  <img
                    src={studentProfile.avatar_url}
                    alt={currentUser?.name}
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                  />
                ) : (
                  <div
                    className="w-24 h-24 rounded-full flex items-center justify-center text-white text-3xl font-semibold border-2 border-gray-200"
                    style={{ backgroundColor: colors.primary }}
                  >
                    {currentUser?.name?.charAt(0) || 'S'}
                  </div>
                )}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                    <Loader className="w-8 h-8 text-white animate-spin" />
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <div>
                <input
                  type="file"
                  id="avatar-upload"
                  accept="image/jpeg,image/jpg,image/png,image/gif,.jpg,.jpeg,.png,.gif"
                  onChange={(e) => handleFileUpload(e, 'avatar')}
                  disabled={uploadingAvatar}
                  className="hidden"
                />
                <label
                  htmlFor="avatar-upload"
                  className={`inline-block px-4 py-2 rounded-lg font-medium transition-colors text-sm ${uploadingAvatar ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-800 hover:bg-gray-300 cursor-pointer'}`}
                >
                  {uploadingAvatar ? 'Uploading...' : 'Upload New Photo'}
                </label>
                <p className="text-xs text-gray-500 mt-2">JPG, PNG or GIF. Max size 2MB.</p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={currentUser?.name || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={currentUser?.email || ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                  onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">LinkedIn URL</label>
                <input
                  type="url"
                  value={formData.linkedinUrl}
                  onChange={(e) => handleInputChange('linkedinUrl', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                  onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold mb-4">Location</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                  onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  maxLength={2}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                  onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                  onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                />
              </div>
            </div>
          </div>

          {/* Track-specific details */}
          {isEmployeeCandidate ? (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold mb-4">Professional Background</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Experience Level
                  </label>
                  <select
                    value={formData.experienceLevel}
                    onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <option value="">Select experience level</option>
                    <option value="0-2 years">0-2 years</option>
                    <option value="3-5 years">3-5 years</option>
                    <option value="6-10 years">6-10 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Professional Interests</label>
                  <input
                    type="text"
                    value={(formData.candidateInterests || []).join(', ')}
                    onChange={(e) => handleInputChange('candidateInterests', e.target.value.split(',').map(v => v.trim()).filter(Boolean))}
                    placeholder="Retirement planning, Portfolio management, Client relationships"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Separate values with commas.</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="border-t border-gray-200 pt-6">
              <h3 className="font-semibold mb-4">Education</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">School</label>
                  <input
                    type="text"
                    value={formData.school}
                    onChange={(e) => handleInputChange('school', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Degree Program</label>
                  <input
                    type="text"
                    value={formData.degreeProgram}
                    onChange={(e) => handleInputChange('degreeProgram', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Year</label>
                  <input
                    type="number"
                    value={formData.graduationYear || ''}
                    onChange={(e) => handleInputChange('graduationYear', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                    onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                    onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Career Preferences */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold mb-4">Career Preferences</h3>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Preferred Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.desiredLocation}
                onChange={(e) => handleInputChange('desiredLocation', e.target.value)}
                placeholder="City, state, or 'Remote'"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
                onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              />
              <p className="text-xs text-gray-500 mt-1">City, state, or 'Remote'</p>
              </div>
                </div>

        {/* Designations / Certifications */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-semibold mb-4">{isEmployeeCandidate ? 'Designations and Licenses' : 'Certifications'}</h3>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                placeholder={isEmployeeCandidate ? 'e.g., CFP, Series 7, Series 66' : 'e.g., Series 7, CFP, CFA'}
                value={certificationInput}
                onChange={(e) => setCertificationInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCertification();
                  }
                }}
                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg focus:border-transparent"
                onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
                onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
              />
              <button
                onClick={addCertification}
                className="px-6 py-2.5 text-white rounded-lg transition-colors font-medium"
                style={{ backgroundColor: colors.primary }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Add
              </button>
            </div>
            {formData.certifications.length > 0 && (
            <div className="flex flex-wrap gap-2">
                {formData.certifications.map((cert) => (
                  <span
                    key={cert}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200"
                >
                  {cert}
                    <button
                      onClick={() => removeCertification(cert)}
                      className="hover:text-red-600 transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
              ))}
            </div>
          )}
          </div>

          {/* Why Interested */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isEmployeeCandidate ? 'Why do you want to join an SEIC member firm?' : 'Why are you interested in financial services?'} <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={5}
              maxLength={500}
              value={formData.whyInterested}
              onChange={(e) => handleInputChange('whyInterested', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:border-transparent resize-none"
              onFocus={(e) => { e.currentTarget.style.boxShadow = `0 0 0 2px ${colors.primary}40`; }}
              onBlur={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                />
            <p className="text-xs text-gray-500 mt-1">
              {formData.whyInterested.length}/500 characters
            </p>
          </div>

          {/* Success/Error Messages */}
          {(uploadError || uploadSuccess) && (
            <div className="border-t border-gray-200 pt-6">
              {uploadError && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {uploadError}
                </div>
              )}
              {uploadSuccess && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700">
                  {uploadSuccess}
                </div>
              )}
            </div>
          )}

          {/* Resume Upload */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Resume</label>
            <input
              type="file"
              id="resume-upload"
              accept=".pdf"
              onChange={(e) => handleFileUpload(e, 'resume')}
              disabled={uploadingResume}
              className="hidden"
            />
            <label
              htmlFor="resume-upload"
              className={`block border-2 border-dashed rounded-lg p-8 text-center transition-colors ${uploadingResume ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300 hover:border-gray-400 cursor-pointer'}`}
            >
              <div className="flex flex-col items-center">
                {uploadingResume ? (
                  <Loader className="w-12 h-12 mb-4 animate-spin" style={{ color: colors.primary }} />
                ) : (
                  <Upload className="w-12 h-12 mb-4 text-gray-400" />
                )}
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium text-gray-900">
                    {uploadingResume ? 'Uploading resume...' : 'Drag and drop your file here, or click to browse'}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-3">PDF format only, up to 5MB</p>
                {studentProfile?.resume_url && (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                    <FileText className="h-3.5 w-3.5" />
                    Resume uploaded
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Video Upload */}
          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">Video Introduction</label>
            <input
              type="file"
              id="video-upload"
              accept="video/mp4,video/quicktime,video/x-msvideo,video/x-ms-wmv,.mp4,.mov,.avi,.wmv"
              onChange={(e) => handleFileUpload(e, 'video')}
              disabled={uploadingVideo}
              className="hidden"
            />
            <label
              htmlFor="video-upload"
              className={`block border-2 border-dashed rounded-lg p-8 text-center transition-colors ${uploadingVideo ? 'border-gray-200 bg-gray-50 cursor-not-allowed' : 'border-gray-300 hover:border-gray-400 cursor-pointer'}`}
            >
              <div className="flex flex-col items-center">
                {uploadingVideo ? (
                  <Loader className="w-12 h-12 mb-4 animate-spin" style={{ color: colors.primary }} />
                ) : (
                  <Upload className="w-12 h-12 mb-4 text-gray-400" />
                )}
                <p className="text-sm text-gray-600 mb-1">
                  <span className="font-medium text-gray-900">
                    {uploadingVideo ? 'Uploading video...' : 'Drag and drop your file here, or click to browse'}
                  </span>
                </p>
                <p className="text-xs text-gray-500 mt-3">Video format (MP4, MOV, AVI, WMV), up to 50MB</p>
                {studentProfile?.video_url && (
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded text-xs font-medium">
                    <Video className="h-3.5 w-3.5" />
                    Video uploaded
                  </div>
                )}
              </div>
            </label>
          </div>
        </div>

        {/* Fixed Bottom Actions */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 md:p-6 shadow-lg">
          <div className="container mx-auto max-w-4xl flex gap-3">
            <button
              onClick={handleCancel}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors font-medium"
            >
              <X className="h-5 w-5" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 text-white rounded-lg transition-colors font-medium"
              style={{ backgroundColor: colors.primary }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              <Check className="h-5 w-5" />
              Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

StudentProfile.propTypes = {
  currentUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    email: PropTypes.string,
    profilePicture: PropTypes.string,
  }),
  studentProfile: PropTypes.shape({
    candidate_track: PropTypes.string,
    phone: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    school: PropTypes.string,
    degree_program: PropTypes.string,
    graduation_year: PropTypes.number,
    employment_type: PropTypes.string,
    experience_level: PropTypes.string,
    desired_location: PropTypes.string,
    candidate_interests: PropTypes.arrayOf(PropTypes.string),
    why_interested: PropTypes.string,
    linkedin_url: PropTypes.string,
    resume_url: PropTypes.string,
    video_url: PropTypes.string,
    certifications: PropTypes.arrayOf(PropTypes.string),
    profile_completion_percentage: PropTypes.number,
  }),
  editMode: PropTypes.bool,
  onSave: PropTypes.func,
  brandLogoUrl: PropTypes.string,
  primaryColor: PropTypes.string,
  secondaryColor: PropTypes.string,
  accentColor: PropTypes.string,
  neutralColor: PropTypes.string,
};

ProgressCircle.propTypes = {
  value: PropTypes.number.isRequired,
  size: PropTypes.number,
  primaryColor: PropTypes.string,
};

export default StudentProfile;

